import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { CalendarIcon, ClipboardList, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { GroupSendRecipientsDrawer } from "@/components/journey/GroupSendRecipientsDrawer";
import { AdminAlert, AdminPageHeader } from "@/components/shell/AdminPage";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { ApiError } from "@/lib/api";
import { listJourneys, type JourneyDefinition } from "@/lib/journey-api";
import {
  defaultExecutionRange,
  filterExecutionLedger,
  localIsoDate,
  parseExecutionStatus,
  type ExecutionStatusFilter,
} from "@/lib/journey-execution-filter";
import { fetchJourneyOverview, type OverviewBatch } from "@/lib/journey-overview";
import { outboundStatusLabel } from "@/lib/journey-overview-format";
import {
  cancelOutboundMessage,
  listOutboundPeers,
  listWecomOutboundMessages,
  type OutboundPeer,
} from "@/lib/wecom-outbound-messages";
import {
  listWecomUsers,
  wecomUserDisplayName,
  type WecomUser,
} from "@/lib/wecom-users";
import { cn } from "@/lib/utils";

const FILTER_ALL = "all";

const STATUS_OPTIONS: { id: ExecutionStatusFilter; label: string }[] = [
  { id: "all", label: "全部状态" },
  { id: "pending_confirm", label: "待企微确认" },
  { id: "upcoming", label: "即将执行" },
  { id: "sent", label: "已发送" },
  { id: "failed", label: "出站失败" },
  { id: "voided", label: "已作废" },
  { id: "cancelled", label: "已取消" },
];

function parseIsoDay(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function DateDayField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  min?: string;
  max?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseIsoDay(value);
  const minDate = min ? parseIsoDay(min) : undefined;
  const maxDate = max ? parseIsoDay(max) : undefined;

  return (
    <div className="w-[10.5rem] shrink-0 space-y-1.5">
      <p className="flex h-7 items-center text-sm font-medium">{label}</p>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-9 w-full justify-start gap-1.5 px-2.5 text-left text-sm font-normal",
              !selected && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="size-3.5 shrink-0 opacity-70" />
            <span className="truncate">
              {selected ? format(selected, "yyyy-MM-dd") : "选择日期"}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start" sideOffset={4}>
          <Calendar
            mode="single"
            locale={zhCN}
            selected={selected}
            onSelect={(day) => {
              if (day) {
                onChange(localIsoDate(day));
                setOpen(false);
              }
            }}
            disabled={(date) => {
              if (maxDate && date > maxDate) return true;
              if (minDate && date < minDate) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Journey 执行 · 触达批次台账 */
export default function JourneyExecutionPage() {
  const { brandId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const status = parseExecutionStatus(searchParams.get("status"));
  const defaults = useMemo(() => defaultExecutionRange(), []);

  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [journeyFilter, setJourneyFilter] = useState(FILTER_ALL);
  const [batches, setBatches] = useState<OverviewBatch[]>([]);
  const [upcoming, setUpcoming] = useState<
    NonNullable<Awaited<ReturnType<typeof fetchJourneyOverview>>["board"]>["upcoming"]
  >([]);
  const [journeys, setJourneys] = useState<JourneyDefinition[]>([]);
  const [wecomUsers, setWecomUsers] = useState<WecomUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [peers, setPeers] = useState<OutboundPeer[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<OverviewBatch | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const customersBase = `/accounts/${accountId}/brands/${brandId}/apps/journey/customers`;
  const senderName = (userid?: string | null) => {
    if (!userid) return "—";
    const user = wecomUsers.find((u) => u.userid === userid);
    return user ? wecomUserDisplayName(user) : userid;
  };

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const [overview, journeyList, users] = await Promise.all([
        fetchJourneyOverview(accountId, { brandId, from, to }),
        listJourneys(accountId, brandId).catch(() => ({ journeys: [] as JourneyDefinition[] })),
        listWecomUsers(accountId, { limit: 200 }).catch(() => ({ wecom_users: [] as WecomUser[] })),
      ]);
      setBatches(overview.batches ?? []);
      setUpcoming(overview.board?.upcoming ?? []);
      setJourneys(journeyList.journeys ?? []);
      setWecomUsers(users.wecom_users ?? []);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "加载执行台账失败");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const journeyId = journeyFilter === FILTER_ALL ? null : Number(journeyFilter);
  const visible = useMemo(
    () =>
      filterExecutionLedger(batches, {
        status,
        journeyId: Number.isFinite(journeyId) ? journeyId : null,
        from,
        to,
      }),
    [batches, from, journeyId, status, to],
  );

  const upcomingVisible = useMemo(() => {
    if (journeyId == null || !Number.isFinite(journeyId)) return upcoming;
    return upcoming.filter((row) => row.journey_definition_id === journeyId);
  }, [journeyId, upcoming]);

  const hasExtraFilter = status !== "all" || journeyFilter !== FILTER_ALL;
  const isUpcoming = status === "upcoming";
  const empty = !loading && (isUpcoming ? upcomingVisible.length === 0 : visible.length === 0);

  const openPeers = async (batch: OverviewBatch) => {
    if (!batch.msgid) {
      toast.message("该批次没有 msgid，无法列出收件人");
      return;
    }
    try {
      const payload = await listWecomOutboundMessages(accountId, {
        msgid: batch.msgid,
        perPage: 100,
      });
      const first = payload.outbound_messages[0];
      if (!first) {
        toast.message("未找到对应出站");
        return;
      }
      const peersPayload = await listOutboundPeers(accountId, first.id);
      setPeers(peersPayload.peers);
      setDrawerOpen(true);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "加载收件人失败");
    }
  };

  const confirmCancelAll = async () => {
    if (!cancelTarget?.msgid) return;
    setCancelling(true);
    try {
      const payload = await listWecomOutboundMessages(accountId, {
        msgid: cancelTarget.msgid,
        perPage: 1,
      });
      const first = payload.outbound_messages[0];
      if (!first) {
        toast.error("找不到待取消出站");
        return;
      }
      await cancelOutboundMessage(accountId, first.id, "all");
      toast.success("已取消该批发送");
      setCancelTarget(null);
      void load();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "取消失败");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="执行"
        description={
          brand ? `「${brand.name}」触达批次台账` : "触达批次台账"
        }
        actions={
          <Button type="button" size="sm" variant="outline" disabled={loading} onClick={() => void load()}>
            <RefreshCw className={`mr-1 size-3.5 ${loading ? "animate-spin" : ""}`} />
            刷新
          </Button>
        }
      />

      {error ? <AdminAlert>{error}</AdminAlert> : null}

      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-end gap-3 border-b border-border px-5 py-4">
          <DateDayField label="开始日期" value={from} onChange={setFrom} max={to} />
          <DateDayField label="结束日期" value={to} onChange={setTo} min={from} />
          <div className="w-[10rem] shrink-0 space-y-1.5">
            <p className="flex h-7 items-center text-sm font-medium">状态</p>
            <Select
              value={status}
              onValueChange={(v) => {
                const next = parseExecutionStatus(v ?? null);
                if (next === "all") setSearchParams({});
                else setSearchParams({ status: next });
              }}
            >
              <SelectTrigger className="w-full" aria-label="按状态筛选">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[12rem] shrink-0 space-y-1.5">
            <p className="flex h-7 items-center text-sm font-medium">旅程</p>
            <Select value={journeyFilter} onValueChange={(v) => setJourneyFilter(v ?? FILTER_ALL)}>
              <SelectTrigger className="w-full" aria-label="按旅程筛选">
                <SelectValue placeholder="全部旅程" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>全部旅程</SelectItem>
                {journeys.map((j) => (
                  <SelectItem key={j.id} value={String(j.id)}>
                    {j.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="px-5 py-10 text-sm text-muted-foreground">加载台账…</p>
        ) : null}

        {!loading && isUpcoming ? (
          upcomingVisible.length === 0 ? (
            <LedgerEmpty hasFilter={hasExtraFilter} />
          ) : (
            <ul className="divide-y divide-border">
              {upcomingVisible.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <div>
                    <p className="font-medium">{row.journey_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.planned_on ?? "—"} · 档 {row.slot_key}
                    </p>
                  </div>
                  <span className="tabular-nums text-muted-foreground">{row.people_count} 人</span>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {!loading && !isUpcoming && empty ? <LedgerEmpty hasFilter={hasExtraFilter} /> : null}

        {!loading && !isUpcoming && visible.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-5 py-2.5 font-medium">日</th>
                  <th className="px-3 py-2.5 font-medium">档</th>
                  <th className="px-3 py-2.5 font-medium">旅程</th>
                  <th className="px-3 py-2.5 font-medium">步骤</th>
                  <th className="px-3 py-2.5 font-medium">人数</th>
                  <th className="px-3 py-2.5 font-medium">状态</th>
                  <th className="px-3 py-2.5 font-medium">跟进人</th>
                  <th className="px-5 py-2.5 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((batch) => (
                  <tr key={batch.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-2.5 tabular-nums">{batch.day}</td>
                    <td className="px-3 py-2.5">{batch.slot_key}</td>
                    <td className="px-3 py-2.5">{batch.journey_name}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{batch.cohort_label}</td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        className="tabular-nums text-foreground underline-offset-2 hover:underline"
                        onClick={() => void openPeers(batch)}
                      >
                        {batch.people_count} 人
                      </button>
                    </td>
                    <td className="px-3 py-2.5">{outboundStatusLabel(batch.status)}</td>
                    <td className="px-3 py-2.5">{senderName(batch.sender_userid)}</td>
                    <td className="px-5 py-2.5">
                      {batch.status === "pending_confirm" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => setCancelTarget(batch)}
                        >
                          取消发送
                        </Button>
                      ) : batch.external_userids[0] ? (
                        <Button type="button" size="sm" variant="ghost" className="h-7" asChild>
                          <Link to={`${customersBase}?q=${encodeURIComponent(batch.external_userids[0])}`}>
                            看客户
                          </Link>
                        </Button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <GroupSendRecipientsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        peers={peers}
        customersBase={customersBase}
      />

      <ConfirmDialog
        open={cancelTarget != null}
        onOpenChange={(open) => {
          if (!open && !cancelling) setCancelTarget(null);
        }}
        title="取消这一批发送？"
        description="将停止企微群发助手里的旧任务，本批所有待确认客户都会取消。已发出的消息无法撤回。"
        confirmLabel="全部取消"
        tone="destructive"
        confirming={cancelling}
        onConfirm={() => void confirmCancelAll()}
      />
    </div>
  );
}

function LedgerEmpty({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-5 py-16 text-center">
      <ClipboardList className="size-8 text-muted-foreground/70" aria-hidden />
      <p className="text-base font-medium text-foreground">
        {hasFilter ? "没有匹配的触达批次" : "本段时间还没有触达批次"}
      </p>
      <p className="max-w-md text-sm text-muted-foreground">
        {hasFilter
          ? "试试调整日期、状态或旅程筛选"
          : "成稿并点「立即发送」后，批次会出现在这里"}
      </p>
    </div>
  );
}
